import React, { useState, useRef } from 'react';
import {
    Typography,
    Box,
    Card,
    CardContent,
    Button,
    CircularProgress,
    Stepper,
    Step,
    StepLabel,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Alert,
    List,
    ListItem,
    ListItemText,
} from '@mui/material';
import { CheckCircle, Undo, Done } from '@mui/icons-material';
import { WarehouseAPI } from '../../services/api';
import { DebundleState } from './types';
import { newOperationId } from '../../utils/operationId';


interface DebundleConfirmationProps {
    state: DebundleState;
    onComplete: () => void;
    onReset: () => void;
}

const DebundleConfirmation: React.FC<DebundleConfirmationProps> = ({
    state,
    onComplete,
    onReset,
}) => {
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [currentOperation, setCurrentOperation] = useState<string | null>(null);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [showErrorDialog, setShowErrorDialog] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Synchronous re-entrancy lock — `isProcessing` state only takes effect on the next
    // render, so a second invocation in the same tick would otherwise run the whole
    // debundle (and its Unicommerce adjustments) twice.
    const debundleInFlightRef = useRef(false);

    const handleExecuteDebundle = async () => {
        if (debundleInFlightRef.current) return;
        debundleInFlightRef.current = true;
        // One id for this whole run; forward steps and their rollbacks use distinct key
        // kinds under it, and each Unicommerce adjustment is allowed to send only once.
        const operationId = newOperationId();
        setIsProcessing(true);
        setError(null);

        // Track what was completed for potential rollback
        let bundleDeducted = false;
        let bundleAllocations: Array<{ shelfCode: string; quantity: number }> = [];
        let childAddResult: any = null;

        try {
            // Step 1: Deduct bundle from Unicommerce
            setCurrentOperation('deduct-bundle');
            
            // Build allocations array from sourceShelfAllocations
            bundleAllocations = (state.sourceShelfAllocations || [])
                .filter(allocation => allocation.allocatedQuantity > 0)
                .map(allocation => ({
                    shelfCode: allocation.shelfCode,
                    quantity: allocation.allocatedQuantity
                }));
            
            // Validate that we have allocations
            if (bundleAllocations.length === 0) {
                throw new Error('No shelf allocations found. Please allocate bundles from source shelves.');
            }
            
            await WarehouseAPI.deductBundleInventoryOnUnicommerce(
                state.selectedBundle!.bundleSku,  // Use bundleSku (actual SKU code), not id (MongoDB ObjectId)
                bundleAllocations,
                operationId
            );
            bundleDeducted = true;

            // Step 2: Add child SKUs back to inventory
            setCurrentOperation('add-children');
            const childAdjustments: Array<{
                skuCode: string;
                shelfCode: string;
                quantity: number;
            }> = [];

            state.childSKUAllocations.forEach((sku) => {
                sku.allocations.forEach((allocation) => {
                    childAdjustments.push({
                        skuCode: sku.skuId,
                        shelfCode: allocation.shelfLocation,
                        quantity: allocation.quantity,
                    });
                });
            });

            childAddResult = await WarehouseAPI.addChildSKUsToInventory(childAdjustments, operationId);
            
            // Check if child SKU addition had failures
            if (!childAddResult.success || (childAddResult.errors && childAddResult.errors.length > 0)) {
                throw new Error(childAddResult.message || 'Child SKU addition failed');
            }

            // Step 3: Log debundle operation
            setCurrentOperation('log');
            const childSkusLogged = state.childSKUAllocations.map((sku) => ({
                sku: sku.skuId,
                qty: sku.totalQuantity,
            }));

            const targetLocations = state.childSKUAllocations.flatMap((sku) =>
                sku.allocations.map((a) => ({
                    shelfLocation: a.shelfLocation,
                    skuCode: sku.skuId,
                    quantity: a.quantity,
                }))
            );

            await WarehouseAPI.logDebundle({
                bundleSku: state.selectedBundle!.bundleSku,  // Use bundleSku for logging
                quantity: state.quantity,
                sourceShelfLocation: state.sourceShelfLocation,
                childSkus: childSkusLogged,
                targetLocations,
            });

            setCurrentOperation(null);
            setShowConfirmDialog(false);
            setShowSuccessModal(true);
        } catch (err: any) {
            console.error('Debundle failed:', err);
            
            // Close the confirmation dialog immediately on error
            setShowConfirmDialog(false);
            
            // Perform rollback if needed
            setCurrentOperation('rollback');
            let rollbackMessage = '';

            try {
                // Rollback child SKU additions if any were successful
                if (childAddResult?.successfulAdjustments && childAddResult.successfulAdjustments.length > 0) {
                    const rollbackChildResult = await WarehouseAPI.rollbackChildSKUAdditions(
                        childAddResult.successfulAdjustments,
                        operationId
                    );
                    if (rollbackChildResult.success) {
                        rollbackMessage += ` Rolled back ${childAddResult.successfulAdjustments.length} child SKU addition(s).`;
                    }
                }

                // Rollback bundle deduction if it was completed
                if (bundleDeducted && bundleAllocations.length > 0) {
                    const rollbackBundleResult = await WarehouseAPI.rollbackBundleDeduction(
                        state.selectedBundle!.bundleSku,
                        bundleAllocations,
                        operationId
                    );
                    if (rollbackBundleResult.success) {
                        rollbackMessage += ` Rolled back bundle deduction from ${bundleAllocations.length} shelf location(s).`;
                    }
                }
            } catch (rollbackErr: any) {
                console.error('Rollback failed:', rollbackErr);
                rollbackMessage += ` WARNING: Rollback partially failed - manual intervention may be required.`;
            }

            const errorMsg = err.message || 'Debundle operation failed';
            setError(`${errorMsg}${rollbackMessage ? '\n\nRollback: ' + rollbackMessage : ''}`);
            setCurrentOperation(null);
            
            // Show error dialog after rollback is complete
            setShowErrorDialog(true);
        } finally {
            debundleInFlightRef.current = false;
            setIsProcessing(false);
        }
    };

    const getOperationSteps = () => {
        const steps = [
            { label: 'Deduct Bundle from Inventory', completed: currentOperation !== 'deduct-bundle' && isProcessing },
            { label: 'Add Child SKUs to Inventory', completed: currentOperation === 'log' || currentOperation === 'rollback' || !isProcessing },
            { label: 'Log Debundle Operation', completed: !isProcessing && !error },
        ];
        
        // Add rollback step if currently rolling back
        if (currentOperation === 'rollback') {
            steps.push({ label: 'Rolling Back Changes...', completed: false });
        }
        
        return steps;
    };

    return (
        <Box>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Undo />
                Confirm Debundle Operation
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Review the debundle details and confirm to proceed.
            </Typography>

            {/* Summary Card */}
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Typography variant="h6" gutterBottom>Debundle Summary</Typography>

                    <Typography variant="body1" sx={{ mb: 2 }}>
                        Breaking down <strong>{state.quantity}</strong> × <strong>{state.selectedBundle?.name}</strong>
                    </Typography>

                    <Typography variant="subtitle2" color="text.secondary">
                        Source Location: <strong>{state.sourceShelfLocation}</strong>
                    </Typography>

                    <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                        Child SKUs to be returned:
                    </Typography>
                    <List dense>
                        {state.childSKUAllocations.map((sku) => (
                            <ListItem key={sku.skuId}>
                                <ListItemText
                                    primary={`${sku.name} × ${sku.totalQuantity}`}
                                    secondary={
                                        sku.allocations.map((a) => `${a.shelfLocation}(${a.quantity})`).join(', ')
                                    }
                                />
                            </ListItem>
                        ))}
                    </List>
                </CardContent>
            </Card>

            {/* Error Display */}
            {error && (
                <Alert severity="error" sx={{ mb: 3, whiteSpace: 'pre-line' }}>
                    <Typography variant="body2" component="div">
                        {error}
                    </Typography>
                </Alert>
            )}

            {/* Execute Button */}
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <Button
                    variant="contained"
                    color="error"
                    size="large"
                    onClick={() => setShowConfirmDialog(true)}
                    disabled={isProcessing}
                    startIcon={<Undo />}
                    sx={{
                        minWidth: 200,
                        py: 1.5,
                    }}
                >
                    Execute Debundle
                </Button>
            </Box>

            {/* Confirmation Dialog */}
            <Dialog open={showConfirmDialog} onClose={() => !isProcessing && setShowConfirmDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Confirm Debundle Operation</DialogTitle>
                <DialogContent>
                    {!isProcessing && (
                        <Typography gutterBottom>
                            Are you sure you want to debundle {state.quantity} × {state.selectedBundle?.name}?
                        </Typography>
                    )}

                    {isProcessing && (
                        <Box sx={{ textAlign: 'center', py: 2 }}>
                            <CircularProgress sx={{ mb: 2 }} />
                            <Typography variant="h6" gutterBottom>
                                Processing Debundle...
                            </Typography>
                            <Stepper activeStep={getOperationSteps().findIndex((step) => !step.completed)} alternativeLabel>
                                {getOperationSteps().map((step, index) => (
                                    <Step key={index} completed={step.completed}>
                                        <StepLabel>{step.label}</StepLabel>
                                    </Step>
                                ))}
                            </Stepper>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    {!isProcessing && (
                        <>
                            <Button onClick={() => setShowConfirmDialog(false)}>Cancel</Button>
                            <Button
                                variant="contained"
                                color="error"
                                onClick={handleExecuteDebundle}
                                disabled={isProcessing}
                            >
                                {isProcessing ? 'Processing...' : 'Confirm & Debundle'}
                            </Button>
                        </>
                    )}
                </DialogActions>
            </Dialog>

            {/* Success Modal */}
            <Dialog
                open={showSuccessModal}
                onClose={() => {
                    setShowSuccessModal(false);
                    onComplete();
                }}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    backgroundColor: '#16A34A',
                    color: 'white'
                }}>
                    <CheckCircle />
                    Debundle Completed Successfully!
                </DialogTitle>
                <DialogContent sx={{ pt: 3, mt: 2 }}>
                    <Typography variant="h6" gutterBottom sx={{ mt: 1 }}>
                        🎉 Bundle has been debundled and inventory updated!
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                        The bundle has been removed from inventory and child SKUs have been returned to their designated shelves.
                    </Typography>

                    <Box sx={{
                        mt: 2,
                        p: 2,
                        backgroundColor: 'rgba(16, 185, 129, 0.1)',
                        border: '1px solid rgba(16, 185, 129, 0.3)',
                        borderRadius: 1
                    }}>
                        <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'success.main' }}>
                            <Done sx={{ color: 'success.main' }} />
                            <strong>Debundle Details:</strong>
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 1, ml: 3 }}>
                            • Bundle: {state.selectedBundle?.name}
                        </Typography>
                        <Typography variant="body2" sx={{ ml: 3 }}>
                            • Quantity: {state.quantity} units
                        </Typography>
                        <Typography variant="body2" sx={{ ml: 3 }}>
                            • Source: {state.sourceShelfLocation}
                        </Typography>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => {
                            setShowSuccessModal(false);
                            onComplete();
                        }}
                        variant="contained"
                        color="success"
                        size="large"
                    >
                        Continue
                    </Button>
                </DialogActions>
            </Dialog>
            {/* Error Dialog */}
            <Dialog
                open={showErrorDialog}
                onClose={() => setShowErrorDialog(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    backgroundColor: 'error.main',
                    color: 'white'
                }}>
                    ⚠️ Debundle Operation Failed
                </DialogTitle>
                <DialogContent sx={{ pt: 3, mt: 2 }}>
                    {error && (
                        <Box>
                            <Typography variant="body1" sx={{ whiteSpace: 'pre-line', mb: 2 }}>
                                {error.split('\n\nRollback:')[0]}
                            </Typography>
                            
                            {error.includes('Rollback:') && (
                                <Alert severity="info" sx={{ mt: 2 }}>
                                    <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                                        Rollback Status:
                                    </Typography>
                                    <Typography variant="body2">
                                        {error.split('\n\nRollback:')[1]}
                                    </Typography>
                                </Alert>
                            )}
                            
                            <Alert severity="warning" sx={{ mt: 2 }}>
                                <Typography variant="body2">
                                    The debundle operation was not completed. All inventory changes have been rolled back.
                                </Typography>
                            </Alert>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button 
                        onClick={() => setShowErrorDialog(false)}
                        variant="contained"
                        color="error"
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>        </Box>
    );
};

export default DebundleConfirmation;
